import { ALL_FILTER_VALUES_CLEARED } from '../../../filterQueryFormat/UsageSubActionTypes';
import { DEFAULT_BUTTON_PROPERTIES } from './constants/quickFilterButtonProperties';
import { USED_FILTERS_BAR } from '../../../filterQueryFormat/UsageActionTypes';
import { getFiltersBarComponents } from '../utils/getFiltersBarComponents';
import { useContext, useMemo, useState, useEffect } from 'react';
import { useOnQuickFilterValueChange } from '../../../hooks/useOnQuickFilterValueChange';
import { useQuickFiltersOnChangeHandler } from '../../../hooks/useQuickFiltersOnChangeHandler';
import { useQuickFiltersVisibility } from '../../../hooks/useQuickFiltersVisibility';
import { useStrategy } from '../../../hooks/useStrategy';
import { useTranslationHelpers } from '../../../hooks/useTranslationHelpers';
import DSFieldRecord from '../../../filterQueryFormat/DSFieldRecord/DSFieldRecord';
import FilterUserAction from '../../../filterQueryFormat/FilterUserAction';
import FiltersBarLayoutManager from './FiltersBarLayoutManager';
import FiltersBarQuickFilters from './FiltersBarQuickFilters';
import SegmentationFiltersInstanceContext from '../../../state/SegmentationFiltersInstanceContext';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import devLogger from 'react-utils/devLogger';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const FiltersBarUI = ({
  advancedFiltersButtonState,
  associatedRecord,
  customPropertyNames,
  defaultPropertyNames,
  enableCustomFilters,
  maxCustomQuickFilters,
  enableReordering,
  forceShowClearQuickFilters,
  onClearAdvancedFilters,
  onClearQuickFilters,
  onCustomPropertyNamesChange,
  onOpenAdvancedFilters,
  showAdvancedFiltersButton,
  showClearAdvancedFiltersButton,
  size
}) => {
  const {
    getTrackerInstance,
    isLoadingViewerData,
    props: {
      baseFilterFamily,
      onUserAction
    },
    quickFiltersInternalValue
  } = useContext(SegmentationFiltersInstanceContext);
  const {
    getField,
    getFields,
    getOperatorsForType,
    getFieldDefinitions
  } = useStrategy();
  const {
    getFieldLabelString
  } = useTranslationHelpers(baseFilterFamily);
  const {
    isQuickFilterVisible,
    onQuickFilterVisibilityChange
  } = useQuickFiltersVisibility();
  const onQuickFiltersChange = useQuickFiltersOnChangeHandler();
  const onValueChange = useOnQuickFilterValueChange(onUserAction, USED_FILTERS_BAR, defaultPropertyNames);
  const defaultButtonProperties = DEFAULT_BUTTON_PROPERTIES[size];
  const allPropertyNames = useMemo(() => {
    const properties = [...defaultPropertyNames, ...customPropertyNames];
    if (associatedRecord && !defaultPropertyNames.includes('associations.labeled')) {
      return ['associations.labeled', ...properties];
    }
    return properties;
  }, [associatedRecord, customPropertyNames, defaultPropertyNames]);
  const fieldDefinitions = getFieldDefinitions(baseFilterFamily);
  const [allFields, setAllFields] = useState();
  const excludedPropertyNames = useMemo(() => {
    return allFields ? allPropertyNames.filter(propertyName => !allFields.get(propertyName)) : [];
  }, [allPropertyNames, allFields]);
  useEffect(() => {
    if (excludedPropertyNames.length === 0) return;
    void (async () => {
      const results = await Promise.all(excludedPropertyNames.map(async propertyName => {
        const field = await getField(baseFilterFamily, propertyName);
        return DSFieldRecord.isDSFieldRecord(field);
      }));
      const invalidPropertyNames = excludedPropertyNames.filter((_, i) => !results[i]);
      if (invalidPropertyNames.length > 0) {
        console.warn(new Error(`Could not render missing properties for ${baseFilterFamily} in FiltersBarUI: ${invalidPropertyNames.join()}`));
        devLogger.warn({
          key: 'Failed to fetch all properties',
          message: `Could not render missing properties for ${baseFilterFamily} in FiltersBarUI: ${invalidPropertyNames.join()}`
        });
      }
    })();
  }, [excludedPropertyNames, baseFilterFamily, getField]);
  useEffect(() => {
    const loadFields = async () => {
      const loadedFields = await getFields(baseFilterFamily);
      setAllFields(loadedFields);
    };
    void loadFields();
  }, [baseFilterFamily, getFields]);
  if (isLoadingViewerData || !allFields) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {});
  }
  const onClearAll = () => {
    var _getTrackerInstance;
    onQuickFiltersChange(undefined);
    (_getTrackerInstance = getTrackerInstance()) === null || _getTrackerInstance === void 0 || _getTrackerInstance.track('clearedAllValues', {
      primaryObjectTypeId: baseFilterFamily,
      version: '2'
    });
    onUserAction(FilterUserAction({
      action: USED_FILTERS_BAR,
      subAction: ALL_FILTER_VALUES_CLEARED,
      filterFamily: baseFilterFamily
    }));
  };
  const propertyRecords = allPropertyNames
  // filter out excluded properties
  .filter(propertyName => !excludedPropertyNames.includes(propertyName))
  // get DSFieldRecords
  .map(propertyName => allFields.get(propertyName)).filter(field => DSFieldRecord.isDSFieldRecord(field))
  // format DSFieldRecords for right label
  .map(field => field.set('displayName', getFieldLabelString(field)));
  const {
    fixedComponents,
    quickFiltersComponents
  } = getFiltersBarComponents({
    advancedFiltersButtonState,
    associatedRecord,
    baseFilterFamily,
    customPropertyNames,
    defaultButtonProperties,
    enableCustomFilters,
    maxCustomQuickFilters,
    enableReordering,
    forceShowClearQuickFilters,
    isQuickFilterVisible,
    getOperatorsForType,
    onClearAdvancedFilters,
    onClearQuickFilters,
    onClearAll,
    onCustomPropertyNamesChange,
    onOpenAdvancedFilters,
    onValueChange,
    propertyRecords,
    quickFiltersInternalValue,
    showAdvancedFiltersButton,
    showClearAdvancedFiltersButton,
    fieldDefinitions
  });

  /*
   * FiltersBarLayoutManager
   *
   * This component updates the state of visibility for
   * each quick filter. It works by rendering all quick filters and fixed
   * components in a flex container that can expand to the parent's full width.
   * Any quick filter that overflows will disappear, which gets detected by
   * an `IntersectionObserver`. Any time a quick filter disappears or
   * reappears, the state from `useQuickFiltersVisibility` gets updated.
   * ---------------------------------------------------------------------------
   * FiltersBarQuickFilters
   *
   * Using the state from `useQuickFiltersVisibility`, it only renders visible
   * quick filters.
   * ---------------------------------------------------------------------------
   * MoreQuickFiltersButton (rendered in `fixedComponents`)
   *
   * Using the state from `useQuickFiltersVisibility`, it only renders invisible
   * quick filters in its popover.
   */
  return /*#__PURE__*/_jsxs(View, {
    blockSize: `${defaultButtonProperties.BUTTON_HEIGHT}px`,
    position: "relative",
    children: [/*#__PURE__*/_jsx(FiltersBarLayoutManager, {
      fixedComponents: fixedComponents,
      onQuickFilterVisibilityChange: onQuickFilterVisibilityChange,
      quickFiltersComponents: quickFiltersComponents
    }), /*#__PURE__*/_jsx(FiltersBarQuickFilters, {
      fixedComponents: fixedComponents,
      isQuickFilterVisible: isQuickFilterVisible,
      quickFiltersComponents: quickFiltersComponents
    })]
  });
};
export default FiltersBarUI;